import { tileDataProp } from 'components/atoms/TileButton';
import { salesTileItemType } from 'components/pages/GrowthAnalysis';
import { tableFirstColumnHeader } from 'components/pages/PVMAnalysis';
import _ from 'lodash';
import { convertNumberStringIntoNumber, reverseObjectKeyValues } from 'utils/helperMethods';

//growth Analysis graph
export function growthAnalysisDownload(graphData: any, salesTile: salesTileItemType) {
    const temp = graphData?.chartData?.data;
    let result: any[] = [];
    //if sales only return 2 items
    if (salesTile.key === 'sales' || salesTile.key === 'depletion') {
        result = temp?.map((item: any) => {
            return [item.category, item.labelSales];
        });
    } else {
        //else return 3 items due to grouped chart
        const halfLength = temp?.length / 2;
        result = temp?.slice(0, halfLength)?.map((item: any, idx: number) => {
            return [item.category, item.labelSales, temp[halfLength + idx]?.labelSales];
        });
    }
    return result;
}

//growth analysis table
export function growthAnalysisTableDownload(data: any, volTile: tileDataProp) {
    let arrOfHeader: any = [];
    let result: any = [];
    if (data) {
        for (let key in data[0]) {
            arrOfHeader.push(key);
        }
        arrOfHeader = arrOfHeader.slice(1);
        arrOfHeader.forEach((key: any) => {
            let temp: any = [key];
            data.forEach((value: any) => {
                temp.push(value[key]?.[0]?.data);
            });
            result.push(temp);
        });
    }

    return result;
}
//promo landScape
export function promoLandScapeDownload(graphData: any[]) {
    const result: any[] = [];
    let tempArr: any[] = [];
    graphData?.forEach((item: any, index: number) => {
        if (index % 2 !== 0) {
            tempArr.push(
                item?.category,
                graphData[index - 1]?.sales,
                item?.sales,
                graphData[index - 1]?.roi,
                item?.roi,
            );
            result.push(tempArr);
            tempArr = [];
        }
    });
    return result;
}

//portFolio Analysis
export function portFolioAnalysisDownload(graphData: any) {
    let result = graphData?.map((item: any) => {
        return [
            item.name,
            item.tactics,
            item.incremental_net_sales,
            item.incremental_profit,
            item.incremental_retailer_profit,
            item.investments,
            item.lift,
            item.roi,
            item.profit_pool,
            item.tpr_acv,
            item.display_acv,
            item.feature_acv,
            item.feature_and_display_acv,
            item.total_acv,
        ];
    });
    return result;
}

//bump report
export function bumpReportDownload(graphData: any) {
    ['Type', 'Date', 'Sales', 'Total sales', 'Value'];
    let result = graphData?.data?.abs?.data?.map((item: any) => {
        return [item.type, item.date, item.sales, item.totalSales, item.value];
    });
    return result;
}

//promo scorecard
export function promoScoreCardTableParser(table: any, tableKeys: any[], selectedAttributeName: string) {
    const allKeys = Object.keys(tableKeys?.[0] || {});

    //removing unnecessary keys
    const requiredKeys = allKeys.slice(0, allKeys.length - 2);

    // export header
    const header = requiredKeys?.map((item: string) => {
        return item === 'name' ? selectedAttributeName : item;
    });

    const result = table?.map((item: any) => {
        return requiredKeys.map((key: string) => item?.[key]);
    });
    return { header, result };
}

export function promoScoreCardDrillDownTableParser(tableData: any, selectedAttributeName: string) {
    const allKeys = Object.keys(tableData?.[0] || {});
    //removing unnecessary keys
    const requiredKeys = allKeys.slice(0, allKeys.length - 1);

    // export header
    const header = requiredKeys?.map((item: string) => {
        return item === 'PPG' ? selectedAttributeName : item;
    });

    const result = tableData?.map((item: any) => {
        return requiredKeys.map((key: string) => item?.[key]);
    });
    return { header, result };
}

//Opportunity Identification
export function opportunityIdentificationDownload(graphData: any, config: any) {
    let result = graphData?.map((item: any) => {
        return [
            item.name,
            isNaN(item.bpe) ? item.bpe : item.bpe * -1,
            isNaN(item.ppe) ? item?.ppe : item.ppe * (config?.reversePpeAxis ? -1 : 1),
            item.mac,
            item.mac_percentage,
            item.rsv,
            item.rsv_growth,
            item.total_volume,
        ];
    });
    return result;
}

//Strategic Opportunity
export function strategicOpportunityDownload(
    graphData: any,
    xAxisLabel: string,
    yAxisLabel: string,
    bubbleColorLabel: string,
) {
    let result = graphData?.map((item: any) => {
        return [
            item.attribute_ex_id ?? '',
            item.name ?? '',
            item.xFieldValue ?? null,
            item.yFieldValue ?? null,
            item.bubbleSizeValue ?? null,
            item.tactics ?? item.name ?? '',
        ];
    });
    return result;
}

//price ladder
export const priceLadderChart1Download = (data: any, selectedLegends: any[], selectedAttribute: string) => {
    const filterLegendsLabel = selectedLegends.map((item) => item?.label);
    const unFilteredLegends = data?.map((attribute: any[]) => {
        return attribute?.filter((item: any) => {
            return !filterLegendsLabel.includes(item?.category);
        });
    });
    const headers: any[] = [selectedAttribute];
    const result = unFilteredLegends?.map((attribute: any, index: number) => {
        const tempNestedResult: any = [];
        attribute.forEach((item: any, nestedIndex: number) => {
            if (nestedIndex === 0) tempNestedResult.push(item?.retailer);
            if (index === 0) headers.push(item?.category);
            if (item?.category?.includes('Sales')) tempNestedResult.push(_.round(item?.sales, 2));
            else tempNestedResult.push(_.isNull(item?.price) ? null : _.round(item?.price, 2));
        });
        return tempNestedResult;
    });
    return { headers, data: result };
};

export const priceLadderChart2Download = (data: any, selectedAttribute: string, kpi: any) => {
    const headers: any[] = [selectedAttribute, kpi];
    const result = data?.map((item: any) => {
        return [item?.category, _.isNull(item?.sales) ? null : _.round(item?.sales, 2)];
    });
    return {
        headers,
        data: result,
    };
};

export const priceLadderTableDownload = (data: any) => {
    const headers = Object.keys(data?.[0] || {}).map((item) => (item === 'type' ? 'KPI' : item));
    const result = data?.map((item: any) => {
        return Object.values(item).map((val: any) =>
            _.isString(val) ? val : _.isNull(val?.value) ? null : _.round(val?.value, 2),
        );
    });
    return { headers, data: result };
};

//PRICE POINT
export const pricePointChartDownload = (data: any, currency: string) => {
    const result: any[][] = [];
    const legends = data?.legends || [];
    for (let i in data?.chartData) {
        const tempRowData: any[] = [];
        const nestedData = data?.chartData[i]?.data;
        const rangeString = `${currency}${data?.chartData[i]?.range?.[0]} - ${currency}${data?.chartData[i]?.range?.[1]}`;
        tempRowData.push(rangeString);
        legends.forEach((item: string) => {
            const filteredData = nestedData?.find((data: any) => {
                return data?.attributeType === item;
            });
            tempRowData.push(_.isNull(filteredData?.value) ? null : _.round(filteredData?.value, 2));
        });
        result.push(tempRowData);
    }
    return {
        headers: ['Range', ...legends],
        data: result,
    };
};

export const pricePointDrilldownDownload = (data: any, attributeType: string, priceRangeText: string) => {
    const headers = [attributeType, ...Object.keys(data?.[0] || {})?.slice(1)];
    const result: any[][] = [];
    data?.forEach((item: any) => {
        const values = Object.values(item).map((data: any) => {
            if (typeof data === 'string') return data;
            return _.isNull(data?.val) ? null : _.round(data?.val, 2);
        });
        result.push(values);
    });
    return {
        headers: headers,
        data: result,
    };
};

export const pricePointTableDownload = (data: any) => {
    const headers = ['', ...Object.keys(data?.[0] || {})?.slice(1, -3)];
    const result = data?.map((item: any) => {
        const values = Object.values(item || {})
            ?.slice(0, -3)
            ?.map((data: any) => {
                if (typeof data === 'string') {
                    return data;
                }
                return _.isNull(data?.value) ? null : _.round(data?.value, 2);
            });
        return values;
    });
    return {
        headers,
        data: result,
    };
};

//PRICE CURVE
export const priceCurveChartDownload = (data: any[], xAxisValues: any[], yAxis: any, xAxis: any) => {
    //console.log(data, xAxisValues, 'logging data');
    const flattenedArray: any[] = [];
    xAxisValues?.forEach((item: any) => {
        const temp: any = { [xAxis?.label]: item };
        data?.filter((val: any) => val?.xAxisData === item).map((xAxisFilteredItem: any) => {
            temp[xAxisFilteredItem['type']] = xAxisFilteredItem[yAxis?.value];
        });
        flattenedArray.push(temp);
    });
    const result = flattenedArray?.map((item) => {
        return Object.values(item || {});
    });
    return {
        headers: Object.keys(flattenedArray?.[0] || {}),
        data: result,
    };
};

export const priceCurveTableDownload = (data: any, level: string) => {
    const headers = ['', level, ...Object.keys(data?.[0] || {})?.slice(2)];
    const result = data?.map((item: any) => {
        const values = Object.values(item || {})?.map((data: any) => {
            if (typeof data === 'string') {
                return data;
            }
            return _.isNull(data?.value) ? null : _.round(data?.value, 2);
        });
        return values;
    });
    return {
        headers: headers,
        data: result,
    };
};

export const priceCurveDrilldownChartDownload = (data: any, xAxis: string) => {
    const headers = [xAxis, 'Index'];
    const result = data?.map((item: any) => {
        return [item?.category, _.isNull(item?.sales) ? null : _.round(item?.sales, 2)];
    });
    return {
        headers,
        data: result,
    };
};

//PRICE TRACKING
export const priceTrackingTableDownload = (data: any, weeks: any[], rows: any, timePeriod: any) => {
    const weeksMap: any = {};
    const headers: any[] = [rows?.label];
    const subHeader: any[] = [''];
    const result: any[][] = [];
    weeks?.forEach((week: any) => {
        weeksMap[week?.value] = week?.label;
    });
    data?.map((item: any, index: number) => {
        const temp: any[] = [];
        Object.keys(item || {})
            .slice(1, -2)
            ?.map((attribute: any) => {
                Object.values(item[attribute]?.nested || {}).forEach((weekLvlData: any) => {
                    temp.push(_.isNull(weekLvlData?.val) ? null : _.round(weekLvlData?.val, 2));
                });
                if (index === 0) {
                    const keys =
                        timePeriod?.value === 'custom'
                            ? [timePeriod?.label]
                            : Object.keys(item[attribute]?.nested || {})?.map((item) => weeksMap[item]);

                    const tempArray = Array(keys.length).fill(attribute);
                    headers.push(...tempArray);
                    subHeader.push(...keys);
                }
            });
        if (index === 0) result.push(subHeader);
        result.push([item?.label, ...temp]);
    });

    return {
        headers,
        data: result,
    };
};

//OPPORTUNITY IDENTIFICATION
export const opportunityIdentificationTrendChartDownload = (data: any[] = []) => {
    const result = Array.isArray(data)
        ? data?.map((item) => {
              return [
                  item?.period,
                  _.isNull(item?.['Current Year']) ? item?.['Current Year'] : _.round(item?.['Current Year'], 2),
                  _.isNull(item?.['Year Ago']) ? item?.['Year Ago'] : _.round(item?.['Year Ago'], 2),
              ];
          })
        : [];
    const headers = ['Period', 'Current Year', 'Year Ago'];
    return {
        headers,
        data: result,
    };
};

export const opportunityIdentificationChart2Download = (data: any[] = [], level: string, legendMapping: any[]) => {
    const colorLegendMapping: any = {};
    const headers = [level, 'Value', 'Type'];
    legendMapping?.forEach((item) => {
        colorLegendMapping[item?.color] = item?.label;
    });
    const result = data?.map((item) => {
        return [
            item?.category,
            _.isNull(item?.sales) ? item?.sales : _.round(item?.sales, 2),
            colorLegendMapping[item?.color],
        ];
    });
    return {
        headers,
        data: result,
    };
};

// we need to traverse all 3 tables and add each rows for export
export function getCombinedTableData(
    levels: any,
    simulationRecommendationDataState: any,
    simulationInputDataState: any,
    simulationOutputDataState: any,
    inputKeyMapping: any,
    recommendationKeyMapping: any,
    scenarioName?: string,
) {
    let tableNamesRow: string[] = ['', '', ''];
    let tableHeadersLabel: string[] = [...levels];
    if (scenarioName) {
        tableHeadersLabel.unshift('Scenario Name');
        tableNamesRow.push('');
    }
    const recommendationHeaders: string[] = [];
    const outputKPIKeys: string[] = [];

    if (simulationRecommendationDataState) {
        Object.keys(recommendationKeyMapping).forEach((item: string, index: number) => {
            tableHeadersLabel.push(recommendationKeyMapping[item].label);
            recommendationHeaders.push(recommendationKeyMapping[item].label);
            tableNamesRow.push(index === 0 ? 'Recommendation' : '');
        });
    }
    const inputKPIMapping = reverseObjectKeyValues(inputKeyMapping);

    Object.keys(inputKPIMapping).forEach((k: string, index: number) => {
        tableNamesRow.push(index === 0 ? 'Input' : '');
        if (k === 'Comp Follow') {
            tableHeadersLabel.push('Comp Follow');
        } else {
            tableHeadersLabel.push('Current ' + k);
            tableHeadersLabel.push('New ' + k);
            tableHeadersLabel.push('Perc Chg');
            tableNamesRow.push(' ');
            tableNamesRow.push(' ');
        }
    });

    if (simulationOutputDataState && simulationOutputDataState?.length > 0) {
        let flag = true;
        Object.keys(simulationOutputDataState[0]).forEach((item: string, index: number) => {
            if (!['label', 'level', 'index', 'nestedIndex'].includes(item)) {
                tableHeadersLabel.push(item);
                outputKPIKeys.push(item);
                if (flag) {
                    tableNamesRow.push('Output');
                    flag = false;
                } else {
                    tableNamesRow.push('');
                }
            }
        });
    }

    const simulationRecommendationDataStateWithLevelInfo = addLevelInformations(
        simulationRecommendationDataState,
        levels,
    );
    let tableContentData: Array<any> = [];

    simulationRecommendationDataStateWithLevelInfo.forEach((data: any, index: number) => {
        //   const singleRow: string[] = [data.label, data.level === levels[0] ? 'Aggregated' : data.level];
        const singleRow: string[] = [data.product, data.retailer];
        if (scenarioName) {
            singleRow.unshift(scenarioName);
        }
        // adding recommendation row data
        recommendationHeaders.forEach((recHeader: string) => {
            singleRow.push(data[recHeader]?.val);
        });

        //  adding input row data
        const inputRowData = simulationInputDataState[index];
        if (inputRowData) {
            const inputKPIMapping = reverseObjectKeyValues(inputKeyMapping);
            // processing kpi keys info
            Object.keys(inputKPIMapping).forEach((key: string, index: number) => {
                const d = inputRowData[key]?.nested;
                if (key === 'Comp Follow') {
                    singleRow.push(d.cp?.val ? 'Yes' : 'No');
                } else {
                    singleRow.push(d.cp?.val || d.cp?.rval);
                    singleRow.push(d.np?.val || d.np?.rval);
                    singleRow.push(d.pc?.val || d.pc?.rval);
                }
            });
        }

        //  adding output row data
        const outputRowData = simulationOutputDataState[index];
        if (outputRowData) {
            // processing kpi keys info
            outputKPIKeys.forEach((key: string, index: number) => {
                singleRow.push(outputRowData[key]?.val);
            });
        }
        // pushing each rowData to the finalData
        tableContentData.push(singleRow);
    });

    convertNumberStringIntoNumber(tableContentData);
    // adding talenames row, headers name and then contentdata
    return [tableNamesRow, tableHeadersLabel, ...tableContentData];
}

// [product,kroger,kroger]
// ['',1,4]
// ['KIND',1.2,4.5]

//price simulator comparison input table
export function priceSimulatorScenarioInputTable(
    data: any,
    groupedKpiKeys: Array<string>,
    levels: Array<string>,
    isCompareMode: boolean,
) {
    const result: any = [];

    const isCompFollowKey = 'Comp Follow';
    if (data?.length > 0) {
        const newData = addLevelInformations(data, levels);
        newData.forEach((item: any) => {
            // adding both levels information
            const data: Array<any> = [item[levels[0]], item[levels[1]]];
            // processing kpi keys info
            groupedKpiKeys.forEach((key, index: number) => {
                const d = item[key]?.nested;

                if (key === isCompFollowKey) {
                    data.push(d.cp?.val ? 'Yes' : 'No');
                } else {
                    if (isCompareMode) {
                        if (key === 'List Price') {
                            data.push(d.cp.val || d.cp.rval);
                        }
                    } else {
                        data.push(d.cp.val || d.cp.rval);
                    }
                    data.push(d.np?.val || d.np?.rval);
                    data.push(d.pc?.val || d.pc?.rval);
                }
            });
            result.push(data);
        });
    }

    convertNumberStringIntoNumber(result);
    return result;
}

export function priceSimulatorScenarioOutputTable(
    data: any,
    groupedKpiKeys: Array<string>,
    levels: Array<string>,
    title?: string,
) {
    const result: any = [];

    if (data?.length > 0) {
        const newData = addLevelInformations(data, levels);
        newData.forEach((item: any) => {
            // adding both levels information
            const data: Array<any> = [item[levels[0]], item[levels[1]]];
            // processing kpi keys info
            groupedKpiKeys.forEach((key) => {
                data.push(item[key]?.val);
            });
            //add scenario name
            if (title) data.unshift(title);
            result.push(data);
        });
    }
    convertNumberStringIntoNumber(result);
    return result;
}

export function priceSimulatorScenarioGoalsTable(data: any, groupedKpiKeys: Array<string>) {
    const result: any = [];

    if (data?.length > 0) {
        data.forEach((item: any) => {
            const d: any[] = [];
            groupedKpiKeys.forEach((key: string) => {
                d.push(item[key] || 0);
            });
            result.push(d);
        });
    }
    convertNumberStringIntoNumber(result);
    return result;
}

function addLevelInformations(data: any, levels: Array<string>) {
    let lastProduct = '';
    const newData = data?.map((i: any) => {
        const product = i.level === levels[1] ? lastProduct : i.label;
        const retailer = i.level === levels[1] ? i.label : 'Aggregated'; // for product call Aggregated

        if (i.level === levels[0]) {
            lastProduct = i.label;
        }

        return {
            product: product,
            retailer: retailer,
            ...i,
        };
    });
    return newData;
}

export function priceSimulatorPortfolioUpdateKpis(data: any, headers: Array<string>) {
    const result: any = [];
    if (data?.length > 0 && headers.length > 0) {
        data.forEach((d: any) => {
            const temp: any = [];
            headers.forEach((header: string, index: number) => {
                if (index === 0) {
                    // label column
                    temp.push(d[header]);
                } else {
                    temp.push(d[header]?.value);
                }
            });
            result.push(temp);
        });
    }

    convertNumberStringIntoNumber(result);
    return result;
}

//GROWTH ANALYSIS DRILLDOWN EXPORTS

export const drilldownChartData = (data: any[], yAxisHeader: string, xAxisHeader: string) => {
    const headers = [yAxisHeader, xAxisHeader];
    const result = data?.map((item: any) => {
        return [item?.category, _.isNull(item?.sales) ? item?.sales : _.round(item?.sales, 2)];
    });
    return {
        data: result,
        headers,
    };
};

export const trendChartDownload = (data: any[] = [], percentageChangeData: any[] = []) => {
    const result = Array.isArray(data)
        ? data?.map((item, index) => {
              return [
                  item?.period,
                  _.isNull(item?.['Current Year']) ? item?.['Current Year'] : _.round(item?.['Current Year'], 2),
                  _.isNull(item?.['Year Ago']) ? item?.['Year Ago'] : _.round(item?.['Year Ago'], 2),
                  _.isNull(percentageChangeData?.[index]?.['sales'])
                      ? percentageChangeData?.[index]?.['sales']
                      : _.round(percentageChangeData?.[index]?.['sales'], 2),
              ];
          })
        : [];
    const headers = ['Period', 'Current Year', 'Year Ago', '% Change'];
    return {
        headers,
        data: result,
    };
};

//sales attribution chart

export const salesAttributionChartDownload = (
    headerData: any[] = [],
    data: any[] = [],
    attributionChangeLabel: string,
) => {
    const headers = [null, 'Previous', ...headerData?.slice(2)];
    let result: any[][] = [];

    let percentageChangeValues: any[] = ['% Change'];
    let attributionChangeValues: any[] = [attributionChangeLabel];
    headers.slice(1).forEach((item: any, index: number) => {
        percentageChangeValues.push(index === 0 ? null : data?.[0]?.[_.snakeCase(item)]);
        const changeData = data?.[1]?.[index === 0 ? 'initial' : _.snakeCase(item)]?.[0]?.change;
        const differenceData = changeData?.[1] - changeData?.[0];
        attributionChangeValues.push(isNaN(differenceData) ? null : _.round(differenceData, 2));
    });

    result.push(percentageChangeValues, attributionChangeValues);

    const headerKeys = headers.slice(1).map((h: any) => _.snakeCase(h));

    const buildRow = (label: string, row: any) => [label, ...headerKeys.map((key, i) => (i === 0 ? null : row?.[key]))];

    const defaultRow = data?.[3]; // default period row (52W)
    if (defaultRow) {
        const scaleLabel = defaultRow?.scale ? String(defaultRow.scale) : '52W';
        result.push(buildRow(`% Change (${scaleLabel})`, defaultRow));
    }

    const deltaRow = data?.[4];
    if (deltaRow) {
        result.push(buildRow('Delta', deltaRow));
    }

    return {
        data: result,
        headers,
    };
};

export const getBestweeksExcelData = (selectedLabel: string, bestWeeksData: any) => {
    const exportData: any[] = [];

    if (bestWeeksData?.bestWeeksMapping) {
        // add all the headers
        const weeksHeader: string[] = [];
        const weeksEventNames: string[] = [];
        bestWeeksData?.bestWeeksMapping?.forEach((item: any) => {
            if (item?.month) {
                weeksHeader.push(item?.month);
                item.periods?.slice(0, item.periods.length - 1).forEach((p: any) => {
                    weeksHeader.push('');
                });

                // events mapping
                item.periods?.forEach((period: any) => {
                    let eventsForWeek: string[] = [];
                    item.events[period].forEach((ev: any) => {
                        eventsForWeek.push(ev.eventName);
                    });
                    weeksEventNames.push(eventsForWeek.join(', '));
                });

                item.periods;
            } else {
                weeksHeader.push('');
                weeksEventNames.push('');
            }
        });
        exportData.push(weeksHeader);
        exportData.push(weeksEventNames);

        // add dropdown value and periods 1-52
        const eventWithWeeknumber: string[] = [selectedLabel];
        Array.from({ length: 52 }).forEach((item: any, index: number) => {
            eventWithWeeknumber.push((index + 1).toString());
        });

        exportData.push(eventWithWeeknumber);

        // add weeks data
        bestWeeksData.weeksData.forEach((weekRw: any) => {
            const weekRowData: any = [weekRw.attributeName];
            Array.from({ length: 52 }).forEach((item: any, index: number) => {
                weekRowData.push(weekRw.values[index + 1].val);
            });
            exportData.push(weekRowData);
        });
    }
    return exportData;
};

//DEPLETION TAB EXPORT
export const getDepletionExportData = (data: any[], y1AxisLabel: string, lineChartCy: string, lineChartYa: string) => {
    const headers = ['Date', y1AxisLabel, lineChartCy, lineChartYa];
    const result = data?.map((item: any) => {
        return [item?.date, item?.areaData, item?.lineDataCy, item?.lineDataYa];
    });

    return {
        headers,
        result,
    };
};

// PVM ANALYSIS table export
export const getPvmImpactExportData = (data: any[]) => {
    const headers: any[] = [tableFirstColumnHeader];
    let result: any[][] = [['']];

    //sub header and header
    Object.keys(data?.[0] || {})
        ?.slice(1)
        .forEach((item: any) => {
            const nested = data?.[0]?.[item]?.nested;
            Object.keys(nested)?.map((nestedItem) => {
                result[0].push(nestedItem);
                headers.push(item);
            });
        });
    //main data
    data?.map((parentItem: any) => {
        const label = parentItem?.label;
        const nestedItemResult = [label];
        Object.keys(parentItem || {})
            ?.slice(1)
            .forEach((item: any) => {
                const nested = parentItem?.[item]?.nested;
                Object.keys(nested)?.map((nestedItem) => {
                    const value = _.round(nested[nestedItem]?.value, 2);
                    nestedItemResult.push(value);
                });
            });
        result.push(nestedItemResult);
    });
    return { headers, result };
};

// ROI portfolio data export
export function portFolioExportData(data: any[], headerKeys: any[]) {
    if (!data || data.length === 0) return []; // Handle empty data
    let result: any[] = [];
    data.forEach((dataItem: any, index: any) => {
        let rowData: any[] = [];
        headerKeys.forEach((headerItem: any) => {
            if (typeof dataItem?.[headerItem?.value] === 'string') {
                rowData.push(dataItem?.[headerItem?.value]);
            } else {
                rowData.push(dataItem?.[headerItem?.value]?.unFormattedValue);
            }
        });
        result.push(rowData);
    });
    return result;
}

// ROI media data export
export function getRoiMediaExportData(data: any, headerKeys: any) {
    if (!data || data.length === 0) return []; // Handle empty data
    let result = data.map((item: any) => {
        let rowData: any[] = [];
        headerKeys.map((val: any, index: any) => {
            rowData.push(item[`${headerKeys[index]}`]?.unFormattedData);
        });
        return rowData;
    });
    return result;
}

export const getPvmImpactChartExportData = (data: any[], tableHeaders: string[]) => {
    return [data?.map((item: any) => item?.sales)];
};

export const getPvmImpactAnalysisChartExportData = (data: any[], tableHeaders: string[]) => {
    if (!tableHeaders) return [];
    const tableHeaderParamKeys = tableHeaders.map((key: string) => key.replace(/ /g, '_').toLocaleLowerCase());
    const firstRowData: any = [];
    const secondRowData: any = ['Sales $'];
    tableHeaderParamKeys.forEach((key: string) => {
        firstRowData.push(data[0][key]);
        const change = data[1][key]?.[0]?.change;
        if (change) secondRowData.push(change[1] - change[0]);
    });
    firstRowData[0] = '% Change';

    return [firstRowData, secondRowData];
};

export const commercialForecastGraphExport = (graphData: any) => {
    const temp = graphData?.chartData?.data;
    let result: any[] = [];
    result = temp?.map((item: any) => {
        const nestedObj: any[] = [];
        item?.map((nestedItem: any, index: number) => {
            if (index === 0) nestedObj.push(nestedItem.category, nestedItem.labelSales);
            //push title as well for index 0 to avoid duplicate titles
            else nestedObj.push(nestedItem.labelSales);
        });
        return nestedObj;
    });
    return result;
};

export function commercialForecastTableExport(data: any) {
    let arrOfHeader: any = [];
    let result: any = [];
    if (data) {
        for (let key in data[0]) {
            arrOfHeader.push(key);
        }
        arrOfHeader = arrOfHeader.slice(1);
        arrOfHeader.forEach((key: any) => {
            let temp: any = [key];
            data.forEach((value: any) => {
                temp.push(value[key]);
            });
            result.push(temp);
        });
    }

    return result;
}

export function getBestWeeksDrillDownExportData(data: any[] = [], columns: any[]) {
    const headers = columns?.map((item: any) => item?.label);
    const result = data?.map((item: any) => {
        return columns?.map((column: any) => item?.[column?.key]);
    });
    return { headers, result };
}
